/**
 * Agent-scoped skill hooks: the enabled-skill list, add/remove/configure.
 * Distinct from `skills-org`, which wraps the org-level skill catalog.
 */

import { hubQueryKeys } from "@lifter/hub-ui/browser-shared";
import { type QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type AgentQueryOptions, useHubApi } from "./context";

export function useSkillsQuery(agentId: string, options: AgentQueryOptions = {}) {
  const api = useHubApi();
  return useQuery({
    queryKey: hubQueryKeys.skills(agentId),
    queryFn: () => api.skills.list(agentId),
    enabled: options.enabled ?? true,
  });
}

export function useAddSkillMutation(agentId: string) {
  const api = useHubApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (skillId: string) => api.skills.add(agentId, skillId),
    onSuccess: () => {
      invalidateSkillQueries(queryClient, agentId);
    },
  });
}

export function useRemoveSkillMutation(agentId: string) {
  const api = useHubApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (skillId: string) => api.skills.remove(agentId, skillId),
    onSuccess: () => {
      invalidateSkillQueries(queryClient, agentId);
    },
  });
}

export function useConfigureSkillMutation(agentId: string) {
  const api = useHubApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ config, skillId }: { skillId: string; config: Record<string, unknown> }) =>
      api.skills.configure(agentId, skillId, config),
    onSuccess: () => {
      invalidateSkillQueries(queryClient, agentId);
    },
  });
}

function invalidateSkillQueries(queryClient: QueryClient, agentId: string) {
  queryClient.invalidateQueries({ queryKey: hubQueryKeys.skills(agentId) });
  queryClient.invalidateQueries({ queryKey: hubQueryKeys.agent(agentId) });
  queryClient.invalidateQueries({ queryKey: hubQueryKeys.agents() });
}
